// Core Migration Client - Handles Edge Function communication and user authentication

import { supabase } from '../../lib/supabaseClient';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { 
  MigrationResponse, 
  MigrationOperation, 
  EntityType, 
  UserInfo, 
  IMigrationClient 
} from './interfaces';

export class MigrationClient implements IMigrationClient {
  private static instance: MigrationClient;
  private supabase = supabase;

  private constructor() {
    console.log('[MIGRATION-CLIENT] Initialized');
  }

  public static getInstance(): MigrationClient {
    if (!MigrationClient.instance) {
      MigrationClient.instance = new MigrationClient();
    }
    return MigrationClient.instance;
  }

  // Get current user info for migration attribution
  private async getCurrentUserInfo(): Promise<UserInfo> {
    try {
      // Try Supabase auth first
      const { data: supabaseUser } = await this.supabase.auth.getUser();
      if (supabaseUser?.user?.id && supabaseUser?.user?.email) {
        console.log('[MIGRATION-CLIENT] Using Supabase authenticated user');
        return {
          userId: supabaseUser.user.id,
          email: supabaseUser.user.email
        };
      } else {
        throw new Error('No Supabase session');
      }
    } catch (supabaseError) {
      console.log('[MIGRATION-CLIENT] No Supabase session, getting user from Amplify for migration...');
      
      // Fallback to Amplify user for migration
      try {
        const amplifyUser = await getCurrentUser();
        const amplifyAttributes = await fetchUserAttributes();
        
        const userId = amplifyUser.userId || amplifyUser.username;
        const email = amplifyAttributes.email || '';
        
        if (!userId || !email) {
          throw new Error('No Amplify user found either');
        }
        
        console.log('[MIGRATION-CLIENT] Using Amplify user for migration');
        return { userId, email };
      } catch (amplifyError) {
        console.error('[MIGRATION-CLIENT] No authenticated user found:', amplifyError);
        throw new Error('No authenticated user found. Please log in to perform migration.');
      }
    }
  }

  // Prepare payload with user attribution and cleanup
  private async preparePayload(payload: Record<string, unknown>, entityType?: EntityType): Promise<Record<string, unknown>> {
    const userInfo = await this.getCurrentUserInfo();
    
    const augmentedPayload: Record<string, unknown> = { ...payload };
    
    // Let Edge Function handle user ID assignment for ALL entities
    // The Edge Function will look up the proper Supabase user ID using _user_email
    // This prevents foreign key constraint violations when Amplify ID ≠ Supabase auth ID
    console.log(`[MIGRATION-CLIENT] Letting Edge Function handle user ID assignment for ${entityType}`);
    
    // Don't override any user ID fields - Edge Function will set them using _user_email lookup
    
    // Add user email for Edge Function to use for user lookup/creation
    augmentedPayload._user_email = userInfo.email;
    
    // Clean up Amplify-specific fields
    delete augmentedPayload.lID; // Remove Amplify location ID
    delete augmentedPayload.owners; // Remove Amplify owners array
    
    console.log(`[MIGRATION-CLIENT] Payload prepared for ${entityType || 'entity'} (user: ${userInfo.userId})`);
    
    // Debug logging for User entities to see exact payload
    if (entityType === 'User') {
      console.log(`[MIGRATION-CLIENT] USER ENTITY PAYLOAD DEBUG:`, {
        hasId: 'id' in augmentedPayload,
        hasCreatedBy: 'created_by' in augmentedPayload,
        hasCreatedBy2: 'createdBy' in augmentedPayload,
        email: augmentedPayload.email,
        userEmail: augmentedPayload._user_email,
        originalAmplifyId: augmentedPayload.original_amplify_id,
        payload: augmentedPayload,
        // payloadKeys: Object.keys(augmentedPayload),
        suspiciousFields: Object.keys(augmentedPayload).filter(key => 
          key.toLowerCase().includes('id') && key !== 'original_amplify_id' && key !== '_user_email'
        )
      });
    }
    
    return augmentedPayload;
  }

  // Invoke Supabase Edge Function
  public async invoke(
    operation: MigrationOperation,
    entityType: EntityType,
    payload: Record<string, unknown>
  ): Promise<MigrationResponse> {
    try {
      const preparedPayload = await this.preparePayload(payload, entityType);

      console.log(`[MIGRATION-CLIENT] Calling Edge Function with payload:`, {
        operation,
        entityType,
        email: preparedPayload.email,
        hasId: 'id' in preparedPayload,
        payload: preparedPayload,
        payloadKeys: Object.keys(preparedPayload)
      });

      const { data, error } = await this.supabase.functions.invoke('migration-services/migration-service', {
        body: { operation, entityType, data: preparedPayload },
      });

      console.log(`[MIGRATION-CLIENT] Edge Function response:`, {
        hasData: !!data,
        hasError: !!error,
        errorDetails: error,
        dataKeys: data ? Object.keys(data) : [],
        responseData: data
      });

      if (error || (data && (data as Record<string, unknown>).success === false)) {
        console.error('[MIGRATION-CLIENT] Edge Function error:', error || (data as Record<string, unknown>)?.error);
        
        // If we have data with success: false, return it as a MigrationResponse instead of throwing
        // This allows the caller to check result.success and result.error
        if (data && (data as Record<string, unknown>).success === false) {
          console.log('[MIGRATION-CLIENT] Returning failed result instead of throwing:', data);
          return data as MigrationResponse;
        }
        
        throw error || new Error((data as Record<string, unknown>)?.error as string || 'Edge function failed');
      }

      return data as MigrationResponse;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[MIGRATION-CLIENT] Failed to invoke migration function:', errorMessage);
      throw err;
    }
  }
} 